import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { get, noop } from 'lodash'
import { useFortisCreateTransactionIntentionMutation } from 'api/fortis/fortisApi'
import { handleError } from 'utils/error'
import { Checkbox, FormControl, FormControlLabel, Grid, useTheme } from '@mui/material'
import FortisSkeleton from 'components/fortis/FortisSkeleton'
import { shallowEqual, useSelector } from 'react-redux'
import LoadingButton from '@mui/lab/LoadingButton'
import T from 'T'
import Constants from 'Constants'

const { DEV_MODE } = Constants
// Lightweight wrapper around Fortis Commerce.js Elements
// Renders a container and initializes Elements with a client token.
// Listens to the `paymentFinished` event and forwards the result to the parent.
const FortisElements = ({
  containerId = 'payment',
  onBeforeSubmit = () => Promise.resolve(true),
  onPaymentFinished = noop,
  amount,
  action = 'sale',
  saveMethod = false,
  onChangeSaveMethod = noop,
  surchargeApplied = false,
  billingAddress = {},
}) => {
  const theme = useTheme()
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)
  const [requestTransactionIntention, { data: transactionIntentionData, isLoading: isLoadingTxn, isUninitialized: isUninitTxn }] =
    useFortisCreateTransactionIntentionMutation()
  const clientToken = useMemo(() => get(transactionIntentionData, 'data.client_token', ''), [transactionIntentionData])
  const hhDescription = useMemo(() => get(transactionIntentionData, 'hhDescription', ''), [transactionIntentionData])
  const hhPaymentId = useMemo(() => get(transactionIntentionData, 'hhPaymentId', ''), [transactionIntentionData])
  const initializedRef = useRef(false)
  const elementsRef = useRef(null)
  const debounceRef = useRef(null)
  // Generation guards to ignore events from stale Elements instances
  const activeInstanceIdRef = useRef(0)
  const isTokenization = useMemo(() => action === 'tokenization', [action])
  const isSale = useMemo(() => action === 'sale', [action])
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  // Fortis turns submit off while its Google Pay sheet is charging, and back on if it's cancelled.
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const submitDisabledRef = useRef(false)
  const [isElementsReady, setIsElementsReady] = useState(false)
  const [isFinished, setIsFinished] = useState(false)
  // Fortis lets a form submit again after an error, and the retry needs an id no earlier sale holds.
  const [formGeneration, setFormGeneration] = useState(0)
  // Keep latest onPaymentFinished without re-initializing Elements
  const onPaymentFinishedRef = useRef(onPaymentFinished)
  useEffect(() => {
    onPaymentFinishedRef.current = onPaymentFinished
  }, [onPaymentFinished])
  const onBeforeSubmitRef = useRef(onBeforeSubmit)
  useEffect(() => {
    onBeforeSubmitRef.current = onBeforeSubmit
  }, [onBeforeSubmit])
  const paymentIdRef = useRef('')
  // Sticky guard: once 'done' or 'error' fires for this Elements instance, do not call
  // onPaymentFinished again, even if Fortis re-emits 'submitted'. Reset only when a new
  // Elements instance is created (see clientToken effect below).
  const finishedOnceRef = useRef(false)
  const submittingRef = useRef(false)

  // Ensure Fortis Commerce.js is loaded before using Elements
  const [isFortisReady, setIsFortisReady] = useState(
    () => typeof window !== 'undefined' && window.Commerce && typeof window.Commerce.elements === 'function'
  )
  useEffect(() => {
    if (typeof window === 'undefined' || isFortisReady) return
    let mounted = true

    const FORTIS_SCRIPT_ID = 'fortis-commercejs'

    const finishIfReady = () => {
      if (!mounted) return
      if (window.Commerce && typeof window.Commerce.elements === 'function') {
        setIsFortisReady(true)
      }
    }

    const onLoad = () => {
      // Defer to ensure global is attached
      setTimeout(finishIfReady, 0)
    }

    const onError = () => {
      handleError(new Error('Failed to load Fortis Commerce.js'))
    }

    let scriptEl = document.getElementById(FORTIS_SCRIPT_ID)

    if (scriptEl) {
      scriptEl.addEventListener('load', onLoad, { once: true })
      scriptEl.addEventListener('error', onError, { once: true })
      // In case it already loaded before handlers attached
      setTimeout(finishIfReady, 0)
    } else {
      console.log(`Dev Mode: ${DEV_MODE}`)
      const src = !DEV_MODE ? 'https://js.fortis.tech/commercejs-v1.0.0.min.js' : 'https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js'

      scriptEl = document.createElement('script')
      scriptEl.id = FORTIS_SCRIPT_ID
      scriptEl.src = src
      scriptEl.async = true
      scriptEl.addEventListener('load', onLoad, { once: true })
      scriptEl.addEventListener('error', onError, { once: true })
      document.head.appendChild(scriptEl)
    }

    return () => {
      mounted = false
    }
  }, [isFortisReady])

  const resetContainer = id => {
    const el = document.getElementById(id)
    if (!el) return
    // Important: do NOT replace the React-managed node; only clear its children
    // This avoids NotFoundError during React unmount when it tries to remove the original node.
    try {
      while (el.firstChild) {
        el.removeChild(el.firstChild)
      }
    } catch (_) {
      // Fallback to innerHTML if direct removal fails
      el.innerHTML = ''
    }
  }

  const unsubscribeAll = () => {
    const elements = elementsRef.current
    if (elements && typeof elements.off === 'function') {
      try {
        elements.off('done')
        elements.off('error')
        elements.off('submitted')
        elements.off('validationError')
        elements.off('ready')
        elements.off('disable_submit')
        elements.off('enable_submit')
      } catch (_) {
        // ignore
      }
    }
  }

  // Debounced fetch of client token; supports 'sale' (requires amount) and 'tokenization' (no amount)
  useEffect(() => {
    if (!isFortisReady || !accountId) {
      return
    }

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    // For sale, require a positive amount; otherwise clear and exit
    if (isSale) {
      if (!amount || amount <= 0 || Number.isNaN(amount)) {
        // Unsubscribe listeners and clear current Elements iframe/content
        unsubscribeAll()
        elementsRef.current = null
        resetContainer(containerId)
        initializedRef.current = false
        activeInstanceIdRef.current = 0
        setIsElementsReady(false)
        setIsFinished(false)
        finishedOnceRef.current = false
        return
      }
    }

    // Before changing the request, unsubscribe from previous elements events
    unsubscribeAll()
    // Dropped now rather than when the new form arrives, so a Pay click in between cannot submit this one.
    elementsRef.current = null

    debounceRef.current = setTimeout(() => {
      const payload = isTokenization
        ? { accountId, businessId, digitalWalletsOnly: false, ach_sec_code: 'WEB', currency_code: 'USD', action: 'tokenization' }
        : {
            accountId,
            businessId,
            digitalWalletsOnly: false,
            ach_sec_code: 'WEB',
            currency_code: 'USD',
            amount,
            action: 'sale',
            // Always tokenize so toggling "Save Method" doesn't tear down the iframe
            // mid-entry. Whether we persist the method is controlled by `savePaymentMethod`
            // in the makePayment payload, not here.
            save_account: true,
          }
      requestTransactionIntention(payload)
    }, 400)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [isTokenization, isSale, amount, containerId, requestTransactionIntention, isFortisReady, accountId, businessId, formGeneration])

  useEffect(() => {
    if (!clientToken || !isFortisReady) return
    // Ensure Commerce is available from the global Fortis script
    const { Commerce } = window
    if (!Commerce || typeof Commerce.elements !== 'function') {
      // Script may not have loaded yet; try again on next effect tick
    }

    try {
      // Clean up any previous Elements instance and container before re-creating
      const containerSelector = `#${containerId}`
      // Unsubscribe from previous instance events before resetting/creating
      unsubscribeAll()
      resetContainer(containerId)
      setIsElementsReady(false)
      setIsFinished(false)
      setIsSubmitLoading(false)
      setIsSubmitDisabled(false)
      finishedOnceRef.current = false
      submittingRef.current = false
      submitDisabledRef.current = false

      // Create new Elements instance
      const elements = new Commerce.elements(clientToken)
      elementsRef.current = elements

      const zipCode = get(billingAddress, 'zipCode', '')

      const options = {
        container: containerSelector,
        theme: 'default',
        language: 'en-us',
        floatingLabels: false,
        ...(!DEV_MODE ? { environment: 'production' } : {}),
        showSubmitButton: false,
        digitalWallets: isTokenization ? [] : ['ApplePay', 'GooglePay'],
        showReceipt: false,
        appearance: {
          fontSize: '12px',
          colorText: theme.palette.text.secondary,
          colorButtonSelectedBackground: theme.palette.primary.main,
          colorButtonActionBackground: theme.palette.primary.main,
          colorButtonText: theme.palette.primary.main,
        },
      }

      if (surchargeApplied) {
        const billingFields = [
          // { name: 'address', required: true, ...(line1 ? { value: line1 } : {}) },
          // { name: 'country', required: true, ...(country ? { value: 'US' } : {}) },
          // { name: 'state', required: true, ...(state ? { value: state } : {}) },
          // { name: 'city', required: false, ...(city ? { value: city } : {}) },
          { name: 'postal_code', required: true, ...(zipCode ? { value: zipCode } : {}) },
        ]
        options.fields = { billing: billingFields }
      }

      const paymentId = isSale ? hhPaymentId : ''
      paymentIdRef.current = paymentId
      options.fields = {
        ...options.fields,
        additional: [
          { name: 'description', value: hhDescription, hidden: true, readOnly: true },
          // Not transaction_api_id: Fortis rejects that on Apple and Google Pay sales. Comes back as transaction_c1.
          ...(paymentId ? [{ name: 'transaction_custom_1', value: paymentId, hidden: true, readOnly: true }] : []),
        ],
      }

      elements.create(options)

      if (typeof elements.on === 'function') {
        let ready = false
        const onDone = result => {
          if (finishedOnceRef.current) return
          finishedOnceRef.current = true
          setIsSubmitLoading(false)
          setIsFinished(true)
          if (onPaymentFinishedRef.current) onPaymentFinishedRef.current(result, paymentId)
        }
        const onError = errorEvent => {
          if (finishedOnceRef.current) return
          finishedOnceRef.current = true
          setIsSubmitLoading(false)
          setIsFinished(true)
          if (onPaymentFinishedRef.current) onPaymentFinishedRef.current(errorEvent, paymentId)
          // Not before ready: a form that fails to load would be replaced by one that fails the same way.
          if (paymentId && ready) {
            unsubscribeAll()
            resetContainer(containerId)
            setFormGeneration(generation => generation + 1)
          }
        }
        const onSubmitted = () => {
          // Do NOT reset finishedOnceRef here — the sticky guard prevents duplicate
          // onPaymentFinished calls if Fortis re-emits 'submitted' after a successful 'done'
          setIsSubmitLoading(true)
        }
        const onValidationError = () => {
          submittingRef.current = false
          setIsSubmitLoading(false)
        }
        const onReady = () => {
          ready = true
          setIsElementsReady(true)
        }
        elements.on('done', onDone)
        elements.on('error', onError)
        elements.on('submitted', onSubmitted)
        elements.on('validationError', onValidationError)
        elements.on('ready', onReady)
        const onSubmitToggled = isDisabled => {
          submitDisabledRef.current = isDisabled
          setIsSubmitDisabled(isDisabled)
        }
        elements.on('disable_submit', () => onSubmitToggled(true))
        elements.on('enable_submit', () => onSubmitToggled(false))
      }
      initializedRef.current = true
    } catch (e) {
      handleError(e)
    }

    // Cleanup on unmount or before next re-create
    return () => {
      // Unsubscribe listeners first
      unsubscribeAll()
      // Fortis Elements has no official destroy API; clean up via DOM operations
      elementsRef.current = null
      resetContainer(containerId)
      setIsElementsReady(false)
      setIsFinished(false)
      finishedOnceRef.current = false
      initializedRef.current = false
    }
  }, [clientToken, containerId, theme, isFortisReady])

  // Show a skeleton while waiting for client token / Elements to be ready
  const shouldShowSkeleton =
    !clientToken && (isLoadingTxn || isUninitTxn) && (action === 'tokenization' || (action === 'sale' && amount > 0))
  if (shouldShowSkeleton) {
    return <FortisSkeleton />
  }

  const handleChangeSaveMethod = event => {
    onChangeSaveMethod(event.target.checked)
  }

  // A wallet charges without this button, so its payment is written by the record call instead.
  const handleSubmit = async () => {
    const elements = elementsRef.current
    if (finishedOnceRef.current || submittingRef.current || submitDisabledRef.current || !elements) return
    const paymentId = paymentIdRef.current
    if (paymentId) {
      submittingRef.current = true
      setIsSubmitLoading(true)
      const written = await onBeforeSubmitRef.current(paymentId)
      // A new form, with an id nothing was written for, may have replaced this one while we waited. It has its own state.
      if (elements !== elementsRef.current) return
      // Or a wallet may have started or finished on this one.
      if (!written || finishedOnceRef.current || submitDisabledRef.current) {
        submittingRef.current = false
        setIsSubmitLoading(false)
        return
      }
    }
    elements.submit()
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <div id={containerId} />
      </Grid>

      {isElementsReady && !isFinished && (
        <>
          {isSale && (
            <Grid item xs={12}>
              <FormControl sx={{ my: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      tabIndex={0}
                      checked={saveMethod}
                      onChange={handleChangeSaveMethod}
                      inputProps={{ 'aria-label': T.SAVE_METHOD }}
                      sx={{
                        '&.Mui-focusVisible, &:focus-visible': theme => ({
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                          borderRadius: 2,
                        }),
                      }}
                    />
                  }
                  label={T.SAVE_METHOD}
                />
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <LoadingButton
              sx={{ mb: 2 }}
              loading={isSubmitLoading}
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
              fullWidth
              variant="contained"
              color="primary"
            >
              {isTokenization ? T.SAVE_METHOD : T.MAKE_PAYMENT}
            </LoadingButton>
          </Grid>
        </>
      )}
    </Grid>
  )
}

FortisElements.propTypes = {
  amount: PropTypes.number,
  action: PropTypes.oneOf(['sale', 'tokenization']),
  containerId: PropTypes.string,
  onBeforeSubmit: PropTypes.func,
  onPaymentFinished: PropTypes.func,
  saveMethod: PropTypes.bool,
  onChangeSaveMethod: PropTypes.func,
  surchargeApplied: PropTypes.bool,
  billingAddress: PropTypes.object,
}

export default FortisElements
